class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const missing = this.renderSections(parsedState, parsedState.sections || {});

    if (this.header) this.header.reveal();
    this.open();

    // If the add response didn't include a usable section (seen with the
    // CHRISTMASPRINT automatic discount active), render it from the current cart.
    if (missing.length) {
      fetch(`${window.location.pathname}?sections=${missing.map((section) => section.id).join(',')}`, {
        cache: 'no-store',
      })
        .then((response) => response.json())
        .then((sections) => this.renderSections(parsedState, sections || {}, missing))
        .catch(() => {});
    }
  }

  // Fills each section from `sections` (id -> HTML); returns the ones it couldn't.
  renderSections(parsedState, sections, only = this.getSectionsToRender()) {
    return only.filter((section) => {
      const target = document.getElementById(section.id);
      const html = sections[section.id];
      if (!target) return false;
      if (!html) return true;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      let source = doc.querySelector(section.selector || '.shopify-section');
      // An automatic Buy X Get Y discount (CHRISTMASPRINT) can split the added
      // line so it no longer has the key the add returned. Fall back to a line
      // of the same variant, preferring the one showing the same custom image.
      if (!source && section.id === 'cart-notification-product') {
        const variantId = String(parsedState.variant_id || parsedState.key || '').split(':')[0];
        const matches = [...doc.querySelectorAll(`[id^="cart-notification-product-${variantId}:"]`)];
        const imageUrl = parsedState.properties && parsedState.properties['_Selected Image URL'];
        source = (imageUrl && matches.find((el) => el.innerHTML.includes(imageUrl))) || matches[0] || null;
      }
      if (!source) return true;
      target.innerHTML = source.innerHTML;
      return false;
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
